import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import Input from 'components/Input'
import Select from 'components/Select'
import Button from 'components/Button'
import { isNilOrEmpty, isNotNilOrEmpty } from 'utils/ramda'
import StudentTeachersList from 'modules/students/components/StudentTeachersList'
import AddStudentTeacherModal from 'modules/students/components/AddStudentTeacherModal'
import { updateStudent } from 'services/StudentsService'
import { fetchSingleStudentRoutine, getActiveStudentsRoutine } from 'modules/students/ducks/actions'
import { selectCurrentStudent } from 'modules/students/ducks/selectors'
import { pick, propOr, update } from 'ramda'
import { selectCurrentTeacher } from 'modules/teachers/ducks/selectors'
import Checkbox from 'components/Checkbox'
import { parseNumber } from 'utils/numbers'
import { STUDENT_ACCOUNTS } from 'utils/student'
import StudentTeachersMarkedToBeDeleted from 'modules/students/components/StudentTeachersMarkedToBeDeleted'
import { updateTeacherAddedStudents } from 'services/TeachersService'

const emptyValues = {
  name: '',
  teachers: [],
  email: '',
  phoneNumber: '',
  comment: '',
  availability: '',
  balance: 0,
  account: 'account1'
}

const EditStudentForm = ({ handleCloseModal, fetchSingle, isTeacherView, isDebtorsView, actionCallback }) => {
  const currentTeacher = useSelector(selectCurrentTeacher)
  const student = useSelector(selectCurrentStudent)
  const [values, setValues] = useState(emptyValues)
  const dispatch = useDispatch()

  const handleValueChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  const callback = () => {
    handleCloseModal()
    setValues(emptyValues)
    if ((isTeacherView && currentTeacher) || isDebtorsView) {
      typeof actionCallback === 'function' && actionCallback()
    } else {
      fetchSingle
        ? dispatch(fetchSingleStudentRoutine({ id: student.id }))
        : dispatch(getActiveStudentsRoutine())
    }
  }

  const handleSubmit = e => {
    e.preventDefault()
    const studentPayload = {
      ...student,
      ...values,
      comment: (values.comment || '').trim(),
      availability: (values.availability || '').trim(),
      balance: parseNumber(values.balance)
    }
    updateStudent({
      student: studentPayload,
      callback
    })
    const valuesTeachers = values.teachers?.map(teacher => teacher.id) || []
    const studentTeachers = student.teachers?.map(teacher => teacher.id) || []
    
    const teachersToUpdate = Array.from(new Set(valuesTeachers.filter(teacher => !studentTeachers.includes(teacher))))

    console.log({valuesTeachers, studentTeachers})

    console.log({teachersToUpdate})

    teachersToUpdate.forEach(teacher => {
      updateTeacherAddedStudents({
        teacherId: teacher,
        student: values
      })
    })
  }

  const handleAddTeacher = teacherValues => {
    handleValueChange('teachers', [
      ...values.teachers,
      teacherValues
    ])
  }

  const handleEditTeacher = (teacherValues, index) => {
    setValues(prev => ({
      ...prev,
      teachers: update(index, teacherValues, prev.teachers)
    }))
  }

  const handleDeleteTeacher = index => {
    setValues(prev => ({
      ...prev,
      teachers: values.teachers.filter((_, i) => i !== index)
    }))
  }

  useEffect(() => {
    student && setValues({
      ...pick([
        'name',
        'teachers',
        'createdAt',
        'email',
        'balance',
        'phoneNumber',
        'comment',
        'account',
      ], student),
      availability: propOr('', 'availability', student),
      toBeDeleted: propOr(false, 'toBeDeleted', student),
      toBeDeletedMarks: propOr([], 'toBeDeletedMarks', student),
      toBeDeletedToggledBy: propOr('admin', 'toBeDeletedToggledBy', student)
    })
  }, [student])

  const handleChangeToggledBy = () => {
    setValues(prev => ({
      ...prev,
      toBeDeletedToggledBy: 'admin'
    }))
  }

  return (
    <>
      <SectionHeader>Dane podstawowe</SectionHeader>
      <Form onSubmit={handleSubmit}>
        <Row>
          <Input
            label='Nazwa ucznia'
            name='name'
            onChange={handleValueChange}
            value={values.name}
          />
          <Input
            label='Numer telefonu'
            name='phoneNumber'
            onChange={handleValueChange}
            value={values.phoneNumber}
          />
          <Input
            label='Email'
            name='email'
            onChange={(name, value) => handleValueChange('email', value.replace(/\s/g, ''))}
            value={values.email}
          />
          <Input
            label='Aktualne saldo'
            name='balance'
            onChange={handleValueChange}
            value={values.balance}
          />
        </Row>
        <Row>
          <Input
            label='Komentarz (opcjonalnie)'
            name='comment'
            onChange={handleValueChange}
            value={values.comment}
            multiline
            rows={6}
            placeholder='Notatki o uczniu...'
          />
          <Input
            label='Dyspozycyjność'
            name='availability'
            onChange={handleValueChange}
            value={values.availability}
            placeholder='np. pon.-pt. 16-20'
            multiline
            rows={6}
          />
        </Row>
        <Row>
          <Select
            label='Konto do wpłat'
            name='account'
            onChange={handleValueChange}
            value={values.account}
            options={Object.entries(STUDENT_ACCOUNTS).map(([value, label]) => ({ value, label }))}
          />
        </Row>
        <Row style={{ alignItems: 'center' }}>
          <Checkbox
            name='toBeDeleted'
            label={`Oznaczony do usunięcia ${values.toBeDeleted ? values.toBeDeletedToggledBy === 'teacher' ? '(przez nauczyciela)' : '(przez admina)' : ''}`}
            value={values.toBeDeleted}
            onChange={handleValueChange}
          />
          {
            values.toBeDeleted && values.toBeDeletedToggledBy === 'teacher' && (
              <ConfirmButton onClick={handleChangeToggledBy}>Potwierdź</ConfirmButton>
            )
          }
        </Row>
        <Row>
          <StudentTeachersMarkedToBeDeleted
            marks={values.toBeDeletedMarks}
            onChange={handleValueChange}
          />
        </Row>
        <ListHeaderWrapper>
          <SectionHeader noMargin>Przypisani nauczyciele</SectionHeader>
          <AddStudentTeacherModal onAddTeacher={handleAddTeacher} />
        </ListHeaderWrapper>
        {isNotNilOrEmpty(values.teachers) && (
          <StudentTeachersList
            onEditTeacher={handleEditTeacher}
            onDeleteTeacher={handleDeleteTeacher}
            teachers={values.teachers}
          />
        )}
        <ButtonsWrapper>
          <Button onClick={handleCloseModal} color='secondary'>Anuluj</Button>
          <Button
            type='submit'
            color='primary'
            disabled={isNilOrEmpty(values.name)}
          >
            Zapisz
          </Button>
        </ButtonsWrapper>
      </Form>
    </>
  )
}

export default EditStudentForm

const Form = styled.form`
  margin-top: 10px;
  width: 100%;
  
  input {
    width: 100%;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;

  button {
    max-width: 200px;
  }
`

const Row = styled.div`
  display: flex;
  width: 100%;
  gap: 20px;
  align-items: center;
  
  ${({ inModal }) => inModal && css`
    flex-direction: column;
    & > div {
      width: 100%;
    }
  `}
`

const SectionHeader = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  margin-top: ${({ noMargin }) => noMargin ? '0' : '30px'};
  color: ${({ theme }) => theme.colors.secondary.main};
`

const ListHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 30px 0 10px;
`

const ConfirmButton = styled.p`
  cursor: pointer;
  transition: all .3s;
  padding-bottom: 15px;
  color: ${({ theme }) => theme.colors.secondary.main};

  &:hover {
    color: ${({ theme }) => theme.colors.primary.main};
  }
`
